'use client';
import React, { useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Mail, ArrowLeft, CheckCircle, AlertCircle } from 'lucide-react';
import Image from 'next/image';
import Link from 'next/link';

export default function ForgotPasswordPage() {
  const { sendPasswordResetEmail } = useAuth();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await sendPasswordResetEmail(email);
      setSent(true);
    } catch (err: any) {
      setError(err?.message || 'Failed to send reset email. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex flex-col items-center justify-center px-6 py-12">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="flex justify-center mb-8">
          <Image
            src="/assets/images/UrbanLogo-Arabic-Final-removebg-preview-1780574243912.png"
            alt="URBANTRADE International Logo"
            width={220}
            height={84}
            className="object-contain"
            priority
          />
        </div>

        <div className="bg-card border border-border rounded-xl p-8 shadow-sm">
          {sent ? (
            /* Success state */
            <div className="text-center">
              <div className="flex justify-center mb-4">
                <div className="w-14 h-14 rounded-full bg-success/10 flex items-center justify-center">
                  <CheckCircle size={28} className="text-success" />
                </div>
              </div>
              <h2 className="text-xl font-700 text-foreground mb-2">Check your email</h2>
              <p className="text-sm text-muted-foreground mb-2">
                We've sent a password reset link to:
              </p>
              <p className="text-sm font-600 text-foreground mb-4">{email}</p>
              <p className="text-xs text-muted-foreground mb-6">
                The link will expire in <span className="font-600 text-foreground">1 hour</span>. 
                If you don't see the email, check your spam folder.
              </p>
              <button
                onClick={() => { setSent(false); setEmail(''); }}
                className="text-xs text-primary hover:text-primary/80 transition-colors"
              >
                Didn't receive it? Send again
              </button>
            </div>
          ) : (
            /* Form state */
            <>
              <div className="mb-6">
                <h2 className="text-xl font-700 text-foreground mb-1">Forgot your password?</h2>
                <p className="text-sm text-muted-foreground">
                  Enter your email address and we'll send you a link to reset your password.
                </p>
              </div>

              {error && (
                <div className="flex items-start gap-2.5 bg-danger/10 border border-danger/20 text-danger rounded-lg px-4 py-3 mb-5 text-sm">
                  <AlertCircle size={16} className="flex-shrink-0 mt-0.5" />
                  <span>{error}</span>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-xs font-600 text-foreground mb-1.5">
                    Email address
                  </label>
                  <div className="relative">
                    <Mail size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      placeholder="you@urbanhub.ae"
                      className="w-full pl-9 pr-3.5 py-2.5 text-sm bg-muted border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary placeholder:text-muted-foreground transition-colors"
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full flex items-center justify-center gap-2 bg-primary text-primary-foreground py-2.5 rounded-lg text-sm font-600 hover:bg-primary/90 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {loading ? (
                    <span className="w-4 h-4 border-2 border-primary-foreground/30 border-t-primary-foreground rounded-full animate-spin" />
                  ) : (
                    <Mail size={15} />
                  )}
                  {loading ? 'Sending…' : 'Send reset link'}
                </button>
              </form>
            </>
          )}
        </div>

        <div className="flex justify-center mt-6">
          <Link
            href="/login"
            className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            <ArrowLeft size={14} />
            Back to Sign in
          </Link>
        </div>

        <p className="text-center text-2xs text-muted-foreground mt-6">
          © 2026 Urban Trade International LLC · All rights reserved
        </p>
      </div>
    </div>
  );
}
